import cx from 'classnames';
import { useAtom } from 'jotai';
import { FunctionComponent, useState } from 'react';
import {
  Zoom,
  Navigation,
  Pagination,
  Keyboard,
  Thumbs,
  FreeMode,
  Mousewheel,
} from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

// Swiper Styles
import 'swiper/css';
import 'swiper/css/bundle';

import useIsMobile from '../../../../hooks/use-is-mobile';
import IconGallery from '../../icons/gallery';

import classes from './image-group.module.css';
import { targetItem } from './state';
import { SwiperButtonZoomIn, SwiperButtonZoomOut } from './zoomButton';
import BPImage from '../../v2/image';

const ImageGallery: FunctionComponent<{
  list: any;
  altExtraInfo?: string;
}> = ({ list, altExtraInfo }) => {
  const [target, setTarget] = useAtom(targetItem);
  const [thumbsSwiper, setThumbsSwiper] = useState<any>(null);
  const isMobile = useIsMobile();

  return (
    <>
      <Swiper
        spaceBetween={0}
        className="w-full lg:w-full h-full max-lg:h-[calc(100%-134px)] flex flex-col swiperConfig"
        slidesPerView={1}
        onSlideChange={(e) => {
          setTarget(e.realIndex);
        }}
        zoom={true}
        navigation={true}
        pagination={{
          clickable: true,
        }}
        initialSlide={target}
        keyboard={{
          enabled: true,
        }}
        thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
        mousewheel={true}
        loop={true}
        modules={[Zoom, Keyboard, Navigation, Pagination, Thumbs, Mousewheel]}
        style={{
          // @ts-ignore
          '--swiper-navigation-color': '#f6f5f5',
          '--swiper-pagination-color': '#f6f5f5',
        }}
      >
        <SwiperButtonZoomIn />
        <SwiperButtonZoomOut />

        <div className="absolute p-2 bg-bparts-100 top-4 left-4 text-base font-bold text-white rounded-md flex gap-2 items-center justify-center z-10">
          <IconGallery className="w-6 h-6" />
          {list.length}
        </div>

        {list.map((img: any, index: number) => (
          <SwiperSlide
            className="bg-neutral-80 w-full h-full aspect-video"
            zoom={true}
            key={`main-slide-${index}`}
          >
            <div className="swiper-zoom-container" data-swiper-zoom="5">
              {/* No Zoom do Swiper, mantemos a tag img nativa para evitar conflitos de ref do Next.js */}
              <img
                className="w-full h-full object-cover"
                src={img.src}
                alt={`${altExtraInfo ? altExtraInfo + ' ' : ''} - Full ${index + 1}`}
                // @ts-ignore - React 18 suporta, mas tipos podem ser chatos
                fetchpriority={index === 0 ? 'high' : 'low'}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      <Swiper
        onSwiper={setThumbsSwiper}
        spaceBetween={10}
        slidesPerView={isMobile ? 3 : 5}
        freeMode={true}
        direction={isMobile ? 'horizontal' : 'vertical'}
        watchSlidesProgress={true}
        centeredSlides={true}
        modules={[FreeMode, Navigation, Thumbs, Mousewheel]}
        mousewheel={true}
        loop={true}
        className={cx(
          'h-[100px] lg:h-full w-[250px] lg:w-[400px] max-lg:w-full max-lg:!px-0 !px-8 !pb-4 gallery-thumbs',
          classes.thumbnailsList
        )}
      >
        {list.map((img: any, index_: number) => (
          <SwiperSlide
            className={cx(
              'h-[100px] lg:h-full w-full lg:w-[400px] aspect-video cursor-pointer',
              classes.imageHover,
              {
                [classes.thumbnailActive]: target === index_,
              }
            )}
            key={`thumb-${index_}`}
          >
            {/* Aqui usamos o BPImage para as thumbnails para tirar partido do cache/unoptimized */}
            <BPImage
              src={img.thumbnail || img.src_big}
              alt={`${altExtraInfo ? altExtraInfo + ' ' : ''} - Thumb ${index_ + 1}`}
              fill
              style={{ objectFit: 'cover' }}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </>
  );
};

export default ImageGallery;
